import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Form, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import moment from "moment";
import axiosInstance from "../../../services/AxiosInstance";
import Api from "../../../constants/Api";
import PageTitle from "../../layouts/PageTitle";
import LoadingCenter from "../../components/Common/LoadingCenter";
import { ApiError } from "../../components/Common/ApiError";
import EmployeeSelect from "../../components/Custom/Main/Employee/EmployeeSelect";
import ReportExportButtons from "../../components/Common/Report/ReportExportButtons";
import EmployeeFinancialSummary from "./EmployeeFinancialSummary";
import InputMoney from "../../components/Forms/Element/InputMoney";
import HintTooltip from "../../components/Common/HintTooltip";
import { SubscriptionRefundAlert, SubscriptionRefundDetails, useSubscriptionRefunds } from "./SubscriptionRefundAlert";
import StatusCheckBox from "../../components/Common/StatusCheckbox";
import EmployeePaymentModal from "../../components/Custom/CashFlow/EmployeePayment/EmployeePaymentModal";
import EmployeePaymentBatchModal from "../../components/Custom/CashFlow/EmployeePayment/EmployeePaymentBatchModal";
import EmployeePaymentCancelModal from "../../components/Custom/CashFlow/EmployeePayment/EmployeePaymentCancelModal";
import { useBranch } from "../../../context/BranchContext";
import useMenuAccess from "../../../hooks/useMenuAccess";
import Module from "../../../constants/Module";
import { convertDoubleToStringPTBR } from "../../../utils/ConverterUtils";
import { formatDate, formatDateTime } from "../../../utils/DateUtils";
import { monthLabel } from "../cash_flow/bonus/bonusMonth";
import './EmployeeStatementReport.css';

/** Atalhos de período. O mês fechado é o caso em que o pote também é apurável. */
const QUICK_PERIODS = {
    this_month: () => [moment().startOf('month'), moment().endOf('month')],
    last_month: () => [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
    last_7_days: () => [moment().subtract(6, 'days'), moment()],
};

const toApiDate = (date) => date.format('YYYY-MM-DD');

const formatCurrency = (value) => `R$ ${convertDoubleToStringPTBR(Number(value || 0))}`;

/** Quantidade fracionária existe para produto vendido por peso, então só mostra decimal quando há. */
const formatQuantity = (value) => {
    const amount = Number(value || 0);
    return Number.isInteger(amount) ? String(amount) : amount.toFixed(2).replace('.', ',');
};

/** Mantém no PDF os destaques laranja do bloco de ganhos usados na tabela do extrato. */
const PRINT_STYLES = `
    @page { size: A4 landscape; margin: 12mm; }
    html, body, * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
    .employee-statement__print-source {
        position: static !important;
        top: auto !important;
        left: auto !important;
        width: 100% !important;
        height: auto !important;
        overflow: visible !important;
        visibility: visible !important;
    }
    .employee-statement__print-report {
        display: block !important;
        padding: 0;
        color: #111 !important;
        background: #fff !important;
        font-size: 10px;
    }
    .employee-statement__print-report h1 {
        margin: 0 0 5px;
        padding-bottom: 8px;
        color: #263238;
        border-bottom: 2px solid #fc8019;
        font-size: 19px;
    }
    .employee-statement__print-report p { margin: 0 0 3px; color: #5f6368; }
    .employee-statement__print-branch { margin-top: 18px; page-break-inside: avoid; }
    .employee-statement__print-branch h2,
    .employee-statement__print-total-title {
        margin: 0 0 6px;
        padding: 6px 10px;
        color: #263238;
        background: #fff5eb;
        border-left: 3px solid #fc8019;
        font-size: 13px;
    }
    .employee-statement__print-total-title { margin-top: 18px; background: #fde6d0; }
    .employee-statement__print-report table {
        width: 100%;
        margin: 0;
        border-collapse: collapse;
        color: #111;
        font-size: 9px;
    }
    .employee-statement__print-report th,
    .employee-statement__print-report td {
        padding: 4px 5px;
        border: 1px solid #cfcfcf;
    }
    .employee-statement__print-report thead th {
        color: #263238;
        background: #eef1f4;
        font-size: 8px;
    }
    .employee-statement__print-report thead th.print-group {
        text-align: center;
        letter-spacing: 0.04em;
        text-transform: uppercase;
    }
    .employee-statement__print-report thead th.print-group--earnings {
        color: #9a4c07;
        background: #fdeadb;
    }
    .employee-statement__print-report tbody tr:nth-child(even) td { background: #fafafa; }
    .employee-statement__print-report tbody tr td.print-earnings,
    .employee-statement__print-report tbody tr th.print-earnings {
        background: #fff7ed !important;
    }
    .employee-statement__print-report tbody tr td.print-emphasis,
    .employee-statement__print-report tbody tr th.print-emphasis,
    .employee-statement__print-report thead th.print-emphasis {
        background: #e6eaef !important;
        font-weight: 700;
    }
    .employee-statement__print-report tbody tr td.print-earnings.print-emphasis,
    .employee-statement__print-report tbody tr th.print-earnings.print-emphasis,
    .employee-statement__print-report thead th.print-earnings.print-emphasis {
        background: #fde1c3 !important;
    }
    .employee-statement__print-report thead th.print-split,
    .employee-statement__print-report tbody tr td.print-split,
    .employee-statement__print-report tbody tr th.print-split {
        border-left: 2px solid #e08a3c;
    }
`;

/**
 * Chave da coluna de uma categoria de serviço. O serviço sem categoria cadastrada ganha
 * coluna própria, para a soma das categorias continuar fechando com a coluna Avulsos.
 */
const categoryKeyOf = (category) => (category.categoryId != null ? String(category.categoryId) : 'none');

/**
 * Faturamento por categoria indexado pela chave da coluna. Linhas e totais (filial e geral)
 * guardam o mesmo formato, então a célula lê qualquer um deles do mesmo jeito.
 */
const toCategoryRevenues = (serviceCategories) => (serviceCategories || []).reduce((revenues, category) => {
    const key = categoryKeyOf(category);
    revenues[key] = (revenues[key] || 0) + Number(category.revenue || 0);
    return revenues;
}, {});

/** Soma dois mapas de faturamento por categoria, para os totais de filial e geral. */
const sumCategoryRevenues = (first = {}, second = {}) => Object.entries(second).reduce((sum, [key, revenue]) => {
    sum[key] = (sum[key] || 0) + revenue;
    return sum;
}, { ...first });

const categoryRevenueFor = (values, key) => Number(values?.categoryRevenues?.[key] || 0);

const EmployeeStatementReport = () => {

    const { t } = useTranslation();

    // A filial ativa vem exclusivamente do cabeçalho/contexto; isso mantém o relatório
    // sincronizado com as demais telas e evita consultar uma filial diferente da exibida.
    const { selectedBranch } = useBranch();
    const branchId = selectedBranch?.id ?? selectedBranch?.value ?? null;
    const [employeeFilter, setEmployeeFilter] = useState(null);

    const [period, setPeriod] = useState(() => {
        const [start, end] = QUICK_PERIODS.this_month();
        return { startDate: toApiDate(start), endDate: toApiDate(end) };
    });
    const [summaries, setSummaries] = useState([]);
    const [loadingSummaries, setLoadingSummaries] = useState(false);
    // Abre a coluna Avulsos do faturamento gerado por categoria de serviço. Sempre começa
    // desligado, em toda visita, porque cada categoria é uma coluna a mais numa tabela que já
    // é larga; a escolha não é lembrada de propósito. O backend sempre manda as categorias,
    // então ligar e desligar não recarrega a lista.
    const [showCategories, setShowCategories] = useState(false);
    const [potPercentage, setPotPercentage] = useState(0);
    // Percentual configurado na empresa. O campo da tela é editável para simular, e o
    // pagamento precisa saber quando o número na tela não é o oficial.
    const [officialPotPercentage, setOfficialPotPercentage] = useState(0);
    const [editableSubscriptionReceived, setEditableSubscriptionReceived] = useState(null);
    const [dashboardSubscriptionReceived, setDashboardSubscriptionReceived] = useState(null);

    // Colaborador aberto no detalhamento. Um por vez: o detalhe é longo e o gestor
    // costuma conferir um caso de cada vez depois de comparar a lista.
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [selectedDetailBranchId, setSelectedDetailBranchId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    // Documento de acerto do profissional, aberto a partir do detalhe já carregado.
    const [showFinancialSummary, setShowFinancialSummary] = useState(false);
    // As listas de serviço e produto são recortadas dentro do detalhe; guardá-las aqui
    // deixa o documento usar exatamente o que a tela mostrou, sem refazer o filtro.
    const [documentItems, setDocumentItems] = useState({ services: [], products: [] });
    const reportRef = useRef(null);
    const detailPrintRef = useRef(null);

    // Pagamentos já registrados no período, para marcar quem recebeu. Registrar e cancelar
    // exigem também o contas a pagar, porque o pagamento vira uma conta baixada lá.
    const canRegisterPayment = useMenuAccess(Module.CASH_FLOW, 'payable_receivable');
    const [payments, setPayments] = useState([]);
    const [paymentTarget, setPaymentTarget] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    // "Pagar todos": paga de uma vez as linhas da lista, com a mesma data, forma e categoria.
    const [showBatchPaymentModal, setShowBatchPaymentModal] = useState(false);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);

    // Só o filtro fica aqui. A ordenação usa o total do faturamento gerado, que depende do
    // rateio das assinaturas calculado mais abaixo; por isso a lista ordenada
    // (visibleSummaries) é montada depois de totalProductionFor. Os agregados abaixo não
    // dependem da ordem e podem usar a lista apenas filtrada.
    const filteredSummaries = summaries
        .filter(item => !employeeFilter || item.employeeId === employeeFilter.value);

    const branchTotals = filteredSummaries.reduce((groups, item) => {
        const key = item.branchId ?? 'all';
        const current = groups[key] || { count: 0, commission: 0, commissionServices: 0, commissionProducts: 0, revenue: 0, subscriptionScore: 0, services: 0, pot: 0, potBonus: 0, directServices: 0, subscriptionServices: 0, products: 0, advances: 0, consumptions: 0, bonuses: 0 };
        groups[key] = {
            count: current.count + 1,
            commission: current.commission + Number(item.commission || 0),
            commissionServices: current.commissionServices + Number(item.commissionServices ?? item.commission ?? 0),
            commissionProducts: current.commissionProducts + Number(item.commissionProducts ?? 0),
            revenue: current.revenue + Number(item.revenue || 0),
            // O rodapé reutiliza subscriptionScore para calcular a parte do pote. Manter o
            // mesmo nome da API evita zerar Assinaturas e, por consequência, o Total.
            subscriptionScore: current.subscriptionScore + Number(item.subscriptionScore || 0),
            services: current.services + Number(item.servicesQuantity || 0),
            pot: current.pot + Number(item.potReward || 0),
            // O backend manda o acordo individual uma vez por colaborador, na primeira linha
            // dele, então somar as linhas aqui não paga o combinado duas vezes.
            potBonus: current.potBonus + Number(item.potBonus || 0),
            directServices: current.directServices + Number(item.directServices || 0),
            subscriptionServices: current.subscriptionServices + Number(item.subscriptionServices || 0),
            products: current.products + Number(item.products || 0),
            advances: current.advances + Number(item.advances || 0),
            consumptions: current.consumptions + Number(item.consumptions || 0),
            bonuses: current.bonuses + Number(item.bonuses || 0),
            // Subtotal da filial nas colunas de categoria. O mapa ausente na primeira linha
            // do grupo é tratado como vazio por sumCategoryRevenues.
            categoryRevenues: sumCategoryRevenues(current.categoryRevenues, item.categoryRevenues)
        };
        return groups;
    }, {});

    const invalidPeriod = moment(period.startDate).isAfter(moment(period.endDate));
    // Mensalidades devolvidas do período: explicam por que o total de Assinaturas pode ficar acima
    // do painel. Uma consulta para a tela toda; o alerta aparece em cada linha de total.
    const subscriptionRefunds = useSubscriptionRefunds(period, branchId, invalidPeriod);
    const [showRefundDetails, setShowRefundDetails] = useState(false);

    const loadSummaries = useCallback(() => {
        if (invalidPeriod) return;

        setLoadingSummaries(true);
        // Trocar o período invalida o detalhe aberto: ele é sempre do mesmo intervalo da lista.
        setSelectedEmployeeId(null);
        setSelectedDetailBranchId(null);
        setDetail(null);

        axiosInstance.get(Api.reports.employee_statement, { params: { ...period, branchId } })
            .then((resp) => {
                // A lista de categorias vira mapa uma vez só, na chegada, para as células e os
                // totais lerem o valor da categoria direto pela chave da coluna.
                const rows = (resp.data || []).map(row => ({
                    ...row,
                    categoryRevenues: toCategoryRevenues(row.serviceCategories)
                }));
                setSummaries(rows);
                // O backend calcula este total no mesmo período/filial da listagem. Usar o
                // revenue-overview aqui misturava o mês corrente com o mês selecionado.
                setDashboardSubscriptionReceived(Number(rows[0]?.subscriptionReceived || 0));
                // O valor inicial acompanha o período consultado; depois disso, a edição é
                // apenas uma previsão local e não deve ser sobrescrita por re-renderizações.
                setEditableSubscriptionReceived(null);
            })
            .catch(error => ApiError(error))
            .finally(() => setLoadingSummaries(false));
    }, [period, branchId, invalidPeriod]);

    useEffect(() => {
        loadSummaries();
    }, [loadSummaries]);

    /**
     * Pagamentos válidos que encostam no período e na filial do cabeçalho.
     *
     * Falha aqui não trava a tela: sem a lista o relatório continua certo, só deixa de marcar
     * quem já recebeu.
     */
    const loadPayments = useCallback(() => {
        if (invalidPeriod) return;

        axiosInstance.get(`${Api.cash_flow.employee_payment}/period`, { params: { ...period, branchId } })
            .then(({ data }) => setPayments(data || []))
            .catch(() => setPayments([]));
    }, [period, branchId, invalidPeriod]);

    useEffect(() => {
        loadPayments();
    }, [loadPayments]);

    /** Pagamentos da pessoa na filial da linha. */
    const paymentsFor = (item) => payments.filter(payment =>
        payment.employee?.id === item.employeeId && payment.branch?.id === item.branchId);

    /** Abre o pagamento da linha; o valor vem da prévia do servidor, não da tela. */
    const openPayment = (item) => {
        setPaymentTarget({
            employeeId: item.employeeId,
            employeeName: item.employeeName,
            branchId: item.branchId,
            branchName: item.branchName
        });
        setShowPaymentModal(true);
    };

    /** Abre o cancelamento de um pagamento já registrado. */
    const openCancelPayment = (payment) => {
        setCancelTarget(payment);
        setShowCancelModal(true);
    };

    useEffect(() => {
        // O percentual inicia com a configuração da empresa, mas continua editável para
        // permitir uma simulação sem alterar o parâmetro oficial nem os lançamentos reais.
        axiosInstance.get(Api.parameter.gamification)
            .then(({ data }) => {
                // O endpoint de parâmetros devolve uma lista, mesmo existindo uma única
                // configuração por empresa; a tela de configurações usa o primeiro registro.
                const configuration = Array.isArray(data) ? data[0] : data;
                setPotPercentage(Number(configuration?.potCommission ?? 0));
                setOfficialPotPercentage(Number(configuration?.potCommission ?? 0));
            })
            .catch(error => ApiError(error));
    }, []);

    useEffect(() => {
        // A troca feita no cabeçalho pode tornar o colaborador selecionado inválido
        // para a nova filial, então o filtro dependente precisa ser limpo.
        setEmployeeFilter(null);
    }, [branchId]);

    const openDetail = (item) => {
        const employeeId = item.employeeId;
        const detailBranchId = item.branchId ?? null;
        // Segundo clique na mesma linha fecha o detalhe.
        if (selectedEmployeeId === employeeId && selectedDetailBranchId === detailBranchId) {
            setSelectedEmployeeId(null);
            setSelectedDetailBranchId(null);
            setDetail(null);
            return;
        }

        setSelectedEmployeeId(employeeId);
        setSelectedDetailBranchId(detailBranchId);
        setDetail(null);
        setLoadingDetail(true);

        axiosInstance.get(`${Api.reports.employee_statement}/${employeeId}`, { params: { ...period, branchId: detailBranchId } })
            .then((resp) => setDetail(resp.data))
            .catch(error => ApiError(error))
            .finally(() => setLoadingDetail(false));
    };

    const applyQuickPeriod = (key) => {
        const [start, end] = QUICK_PERIODS[key]();
        setPeriod({ startDate: toApiDate(start), endDate: toApiDate(end) });
    };

    const isQuickPeriodActive = (key) => {
        const [start, end] = QUICK_PERIODS[key]();
        return period.startDate === toApiDate(start) && period.endDate === toApiDate(end);
    };

    const totals = filteredSummaries.reduce((acc, item) => ({
        revenue: acc.revenue + Number(item.revenue || 0),
        commission: acc.commission + Number(item.commission || 0),
        commissionServices: acc.commissionServices + Number(item.commissionServices ?? item.commission ?? 0),
        commissionProducts: acc.commissionProducts + Number(item.commissionProducts ?? 0),
        subscriptionScore: acc.subscriptionScore + Number(item.subscriptionScore || 0),
        potReward: acc.potReward + Number(item.potReward || 0),
        potBonus: acc.potBonus + Number(item.potBonus || 0),
        directServices: acc.directServices + Number(item.directServices || 0),
        subscriptionServices: acc.subscriptionServices + Number(item.subscriptionServices || 0),
        products: acc.products + Number(item.products || 0),
        advances: acc.advances + Number(item.advances || 0),
        consumptions: acc.consumptions + Number(item.consumptions || 0),
        bonuses: acc.bonuses + Number(item.bonuses || 0),
        categoryRevenues: sumCategoryRevenues(acc.categoryRevenues, item.categoryRevenues),
    }), { revenue: 0, commission: 0, commissionServices: 0, commissionProducts: 0, subscriptionScore: 0, potReward: 0, potBonus: 0, directServices: 0, subscriptionServices: 0, products: 0, advances: 0, consumptions: 0, bonuses: 0, categoryRevenues: {} });

    // O pote só é apurável em mês fechado, então a coluna some junto com o valor.
    const showPot = filteredSummaries.some(item => item.potReward !== null && item.potReward !== undefined);
    // A coluna do adicional só existe quando alguém tem acordo no período. Numa barbearia sem
    // acordo nenhum ela seria uma coluna inteira de R$ 0,00 espremendo as outras onze.
    const showBonus = filteredSummaries.some(item => Number(item.potBonus || 0) > 0);
    // Bonificação também só aparece quando alguém recebe no período: a maioria das casas não
    // usa, e uma coluna inteira de R$ 0,00 só espremeria as outras.
    const showEmployeeBonus = filteredSummaries.some(item => Number(item.bonuses || 0) > 0);
    // Vale e líquido são colunas fixas: o operador espera encontrá-las sempre no mesmo
    // lugar, e uma coluna que aparece e some conforme o período confunde mais do que o
    // espaço que economiza. Sem vale no período elas mostram R$ 0,00.
    // Consumo da equipe (refrigerante, água) é recurso novo e muitas casas não usam. Ao
    // contrário do vale, a coluna só aparece quando alguém consumiu no período, pelo mesmo
    // motivo do adicional: uma coluna inteira de R$ 0,00 só espreme as outras.
    const showConsumptions = filteredSummaries.some(item => Number(item.consumptions || 0) > 0);
    // Colunas de categoria, quando o gestor marca a opção: todas as categorias que aparecem
    // em alguma linha visível, em ordem alfabética como no quadro do detalhamento. A mesma
    // lista vale para todas as linhas, e quem não vendeu na categoria mostra R$ 0,00.
    const categoryColumns = showCategories
        ? Object.values(filteredSummaries.reduce((columns, item) => {
            (item.serviceCategories || []).forEach(category => {
                const key = categoryKeyOf(category);
                columns[key] = columns[key] || { key, name: category.categoryName || t('uncategorized') };
            });
            return columns;
        }, {})).sort((first, second) => first.name.localeCompare(second.name, 'pt-BR'))
        : [];
    // Bloco de faturamento: as categorias (quando ligadas), avulsos, produtos, assinaturas e total.
    const revenueColumnCount = 4 + categoryColumns.length;
    /**
     * Células de categoria de uma linha, do subtotal da filial ou do total geral, na ordem
     * do cabeçalho. Ficam antes de Avulsos porque são as partes dele: lidas da esquerda para
     * a direita, as categorias somam a coluna que vem logo depois.
     */
    const renderCategoryCells = (values, emphasize = false) => categoryColumns.map(column => (
        <td key={column.key} className="text-end employee-statement__cell--category">
            {emphasize
                ? <strong>{formatCurrency(categoryRevenueFor(values, column.key))}</strong>
                : formatCurrency(categoryRevenueFor(values, column.key))}
        </td>
    ));
    // Colunas do bloco de ganhos: avulsos, produtos, assinaturas, total, vales e líquido,
    // mais adicional, bonificação e consumos quando existem.
    const earningsColumnCount = 6 + (showBonus ? 1 : 0) + (showEmployeeBonus ? 1 : 0) + (showConsumptions ? 1 : 0);
    // Número de colunas da tabela (nome e seta somam as duas além dos blocos), usado nos
    // colSpan das linhas de agrupamento.
    const columnCount = 2 + revenueColumnCount + earningsColumnCount;
    // Colunas de valor: as de faturamento mais as de ganhos.
    const numericColumnCount = revenueColumnCount + earningsColumnCount;
    // Larguras calculadas para somar sempre 100%. Antes cada coluna tinha um percentual fixo e a
    // coluna Consumos só somava o dela: o total passava de 100% e o navegador espremia todas,
    // a mais nova mais que as outras. Com colunas extras o nome cede um pouco de espaço.
    const columnWidths = (() => {
        const name = numericColumnCount > 10 ? 16 : 20;
        const chevron = 2.5;
        return { name, chevron, numeric: (100 - name - chevron) / numericColumnCount };
    })();
    // A base da assinatura é da empresa e vem igual em todas as linhas, então pegar a
    // primeira da lista filtrada (ainda sem ordem) dá o mesmo valor.
    const subscriptionReceived = dashboardSubscriptionReceived ?? Number(filteredSummaries[0]?.subscriptionReceived || 0);
    const displayedSubscriptionReceived = editableSubscriptionReceived == null
        ? subscriptionReceived
        : Number(editableSubscriptionReceived) || 0;
    // Base ou percentual do pote alterados na tela: o Líquido exibido é simulação, e o
    // pagamento avisa que vai usar o valor oficial.
    // A base compara em centavos, como o campo mostra: digitar o mesmo valor oficial não é simulação.
    const isSimulating = (editableSubscriptionReceived != null
            && Math.round(Number(editableSubscriptionReceived) * 100) !== Math.round(subscriptionReceived * 100))
        || Number(potPercentage) !== Number(officialPotPercentage);
    // O Pote é rateado por pontos de assinatura, não pelo valor dos itens da comanda.
    // Benefício integral costuma ter item com R$ 0,00, mas continua gerando pontuação.
    // A base usa todas as linhas, mesmo quando o gestor filtra um colaborador, para que
    // selecionar uma pessoa não transforme a participação dela artificialmente em 100%.
    const totalSubscriptionScore = summaries.reduce(
        (total, item) => total + Number(item.subscriptionScore || 0), 0);
    const subscriptionRewardPerPoint = totalSubscriptionScore > 0
        ? (displayedSubscriptionReceived * ((Number(potPercentage) || 0) / 100)) / totalSubscriptionScore
        : 0;
    const subscriptionRewardFor = (item) => Number(item.subscriptionScore || 0) * subscriptionRewardPerPoint;
    // Mantém compatibilidade durante uma publicação gradual: antes do backend novo chegar,
    // a comissão geral ainda aparece como Avulso em vez de deixar a coluna vazia.
    const ordinaryCommissionFor = (item) => Number(item.commissionServices ?? item.commission ?? 0);
    const productCommissionFor = (item) => Number(item.commissionProducts ?? 0);
    const subscriptionEarningsFor = (item) => subscriptionRewardFor(item);
    // Acordo individual do Pote: percentual combinado com a casa, pago pela parte da empresa.
    // Entra no ganho bruto porque é dinheiro que o colaborador recebe no mês; deixá-lo de
    // fora faria o relatório mostrar um total menor do que o pagamento.
    const potBonusFor = (item) => Number(item.potBonus || 0);
    // Bonificação: valor mensal combinado com a casa, com vigência. Vem pronta do backend pela
    // mesma regra do pagamento e do portal (um crédito por mês, no dia 1), e soma no ganho.
    const bonusesFor = (item) => Number(item.bonuses || 0);
    const totalEarningsFor = (item) =>
        ordinaryCommissionFor(item) + productCommissionFor(item) + subscriptionEarningsFor(item)
        + potBonusFor(item) + bonusesFor(item);
    // Vale já retirado no período. Vem do backend por linha (colaborador/filial), então o
    // recorte é o mesmo das outras colunas.
    const advancesFor = (item) => Number(item.advances || 0);
    // O que sobra a pagar. Pode ficar negativo quando o vale passou do ganho do período, e
    // esse sinal é a informação: o colaborador retirou mais do que rendeu.
    // Consumos pendentes do período: o que ele pegou na casa e não pagou no caixa. Desconta
    // igual ao vale; o que ele pagou na hora não chega aqui.
    const consumptionsFor = (item) => Number(item.consumptions || 0);
    const netEarningsFor = (item) => totalEarningsFor(item) - advancesFor(item) - consumptionsFor(item);
    // A mensalidade pertence à empresa, mas a produção precisa reconhecer quem atendeu o
    // assinante. O rateio por pontos usa a mesma régua já aplicada ao pote e não duplica a
    // mensalidade entre colaboradores.
    // O backend centraliza o rateio para que dashboard e relatório não tenham duas contas
    // diferentes. O fallback mantém compatibilidade durante a atualização da API.
    const subscriptionProductionFor = (item) => item.subscriptionProduction != null && editableSubscriptionReceived == null
        ? Number(item.subscriptionProduction)
        : Number(item.subscriptionScore || 0) * (
            totalSubscriptionScore > 0 ? displayedSubscriptionReceived / totalSubscriptionScore : 0
        );
    const totalProductionFor = (item) => Number(item.revenue || 0) + subscriptionProductionFor(item);
    // Filial primeiro, porque a tabela agrupa por ela. Dentro da filial, do maior para o
    // menor Total do faturamento gerado (avulsos + produtos + assinaturas), o mesmo valor
    // da coluna. Antes ordenava pela comissão de avulsos e produtos, que deixava de bater
    // com o que aparece na tela quando a assinatura pesa. Empate cai no nome, para a ordem
    // não mudar a cada recarga no começo do mês, quando muita gente ainda está zerada.
    // Como o rateio é recalculado ao editar a base da assinatura, a ordem acompanha a simulação.
    const visibleSummaries = [...filteredSummaries].sort((first, second) => {
        const branchOrder = (first.branchName || '').localeCompare(second.branchName || '', 'pt-BR');
        return branchOrder
            || totalProductionFor(second) - totalProductionFor(first)
            || (first.employeeName || '').localeCompare(second.employeeName || '', 'pt-BR');
    });
    const maxEarningsByBranch = visibleSummaries.reduce((values, item) => {
        const key = item.branchId ?? 'all';
        values[key] = Math.max(values[key] || 0, totalEarningsFor(item));
        return values;
    }, {});

    // A planilha não tem como mesclar o cabeçalho de dois níveis da tela, e uma linha
    // extra de grupos atrapalharia filtro e tabela dinâmica. O bloco vira prefixo do
    // nome da coluna, que resolve a mesma ambiguidade em uma linha só.
    const exportHeaders = [
        t('branch'), t('employee'),
        // A planilha leva as categorias só quando estão na tela, pelo mesmo motivo do vale:
        // quem exporta confere o arquivo contra o que estava vendo.
        ...categoryColumns.map(column => `Faturamento: ${column.name}`),
        'Faturamento: avulsos', 'Faturamento: produtos', 'Faturamento: assinaturas', 'Faturamento: total',
        'Ganhos: avulsos', 'Ganhos: produtos', 'Ganhos: assinaturas',
        ...(showBonus ? ['Ganhos: adicional'] : []),
        ...(showEmployeeBonus ? ['Ganhos: bonificação'] : []),
        'Ganhos: total',
        // Vale e líquido só entram quando existe vale no período, na mesma regra da tela:
        // a planilha precisa ter as mesmas colunas de quem exportou, senão a conferência
        // não bate com o que estava na tela.
        'Ganhos: vales',
        ...(showConsumptions ? ['Ganhos: consumos'] : []),
        'Ganhos: líquido',
        // A planilha é o lugar da conferência fina, então guarda também o que a tela
        // não mostra: a pontuação e o pote realmente apurado no fechamento do mês.
        t('gamification.score'), ...(showPot ? ['Pote apurado'] : []),
        t('services_performed')
    ];

    /** Colunas de valor de uma linha da planilha, na mesma ordem do cabeçalho. */
    const exportValues = (values) => [
        ...categoryColumns.map(column => formatCurrency(categoryRevenueFor(values, column.key))),
        formatCurrency(values.directServices),
        formatCurrency(values.products),
        formatCurrency(subscriptionProductionFor(values)),
        formatCurrency(totalProductionFor(values)),
        formatCurrency(ordinaryCommissionFor(values)),
        formatCurrency(productCommissionFor(values)),
        formatCurrency(subscriptionEarningsFor(values)),
        ...(showBonus ? [formatCurrency(potBonusFor(values))] : []),
        ...(showEmployeeBonus ? [formatCurrency(bonusesFor(values))] : []),
        formatCurrency(totalEarningsFor(values)),
        formatCurrency(advancesFor(values)),
        ...(showConsumptions ? [formatCurrency(consumptionsFor(values))] : []),
        formatCurrency(netEarningsFor(values))
    ];

    const exportRows = visibleSummaries.reduce((rows, item, index) => {
        rows.push([
            item.branchName || t('all_branches'),
            item.employeeName,
            ...exportValues(item),
            item.subscriptionScore || 0,
            ...(showPot ? [item.potReward != null ? formatCurrency(item.potReward) : '-'] : []),
            formatQuantity(item.servicesQuantity)
        ]);
        const next = visibleSummaries[index + 1];
        if (!next || next.branchId !== item.branchId) {
            const subtotal = branchTotals[item.branchId ?? 'all'];
            rows.push([
                item.branchName || t('all_branches'),
                `${t('total')} ${item.branchName || t('all_branches')}`,
                ...exportValues(subtotal),
                subtotal.subscriptionScore,
                ...(showPot ? [formatCurrency(subtotal.pot)] : []),
                formatQuantity(subtotal.services)
            ]);
        }
        return rows;
    }, []);

    // A planilha fecha com o total geral: quem abre o arquivo direto não tem o rodapé
    // fixo da tela para conferir.
    if (visibleSummaries.length > 0) {
        exportRows.push([
            '',
            'Total geral',
            ...exportValues(totals),
            totals.subscriptionScore,
            ...(showPot ? [formatCurrency(totals.potReward)] : []),
            formatQuantity(visibleSummaries.reduce((sum, item) => sum + Number(item.servicesQuantity || 0), 0))
        ]);
    }


    // Renderizado dentro do próprio tbody, logo abaixo da linha do colaborador: o detalhe
    // precisa ficar junto de quem ele descreve, senão o gestor perde a referência ao rolar.
    const renderDetailRow = (columnCount, row) => {
        // O endpoint já filtra serviços, mas o filtro defensivo evita que uma versão antiga
        // do backend misture produtos na seção de Avulsos durante uma publicação gradual.
        const allCommissionItems = detail?.commissionItems || [];
        // Item de categoria que não comissiona (água, chiclete, chocolate) fica fora das
        // listas de comissão: ele nunca terá valor ali, e no meio das linhas que pagam só
        // atrapalha quem confere. A venda continua no faturamento e em serviços realizados,
        // e o total não muda, porque essas linhas valem R$ 0,00.
        const commissionItems = allCommissionItems.filter(item => item.commissionVeto !== true);
        const typedServiceItems = commissionItems.filter(item =>
            String(item.product?.type || '').toUpperCase() === 'SERVICE');
        const productCommissionItems = commissionItems.filter(item =>
            String(item.product?.type || '').toUpperCase() === 'PRODUCT');
        // Algumas respostas antigas não serializam o tipo do produto. Nesse caso, o backend
        // já garante que a lista é de serviços; o fallback evita esconder todo o detalhe.
        const serviceCommissionItems = typedServiceItems.length > 0 || commissionItems.length === 0
            ? typedServiceItems
            : commissionItems;
        // O detalhamento continua mostrando cada atendimento, mas este resumo permite
        // conferir de quais categorias o faturamento e a comissão avulsa são compostos.
        const servicesByCategory = serviceCommissionItems.reduce((groups, item) => {
            const categoryName = item.product?.category?.name || t('uncategorized');
            const current = groups[categoryName] || { revenue: 0, commission: 0 };
            groups[categoryName] = {
                revenue: current.revenue + Number(item.total || 0),
                commission: current.commission + Number(item.commissionValue || 0)
            };
            return groups;
        }, {});
        const categoryTotals = Object.entries(servicesByCategory)
            .sort(([first], [second]) => first.localeCompare(second, 'pt-BR'));
        const rowPayments = paymentsFor(row);

        return (
        <tr className="employee-statement__detail-row">
            <td colSpan={columnCount}>
                <div ref={detailPrintRef} className="employee-statement__detail">
                    {detail && (
                        <div className="employee-statement__detail-actions d-print-none">
                            <ReportExportButtons
                                contentRef={detailPrintRef}
                                documentTitle={`extrato-${detail.summary.employeeName}`}
                                fileName={`extrato-${detail.summary.employeeName}`}
                                preambleRows={[
                                    [t('employee'), detail.summary.employeeName],
                                    [t('branch'), detail.summary.branchName || t('all_branches')],
                                    ['Período', `${moment(period.startDate).format('DD/MM/YYYY')} a ${moment(period.endDate).format('DD/MM/YYYY')}`]
                                ]}
                                headers={['Seção', 'Data', 'Cliente', 'Serviço', 'Categoria', 'Quantidade', 'Total', 'Comissão/Pontos']}
                                rows={[
                                    ...serviceCommissionItems.map(item => [
                                        'Comissão',
                                        formatDateTime(item.createdAt),
                                        item.customerName,
                                        item.product?.name,
                                        item.product?.category?.name || '-',
                                        formatQuantity(item.qty),
                                        formatCurrency(item.total),
                                        formatCurrency(item.commissionValue ?? 0)
                                    ]),
                                    ...productCommissionItems.map(item => [
                                        'Produtos',
                                        formatDateTime(item.createdAt),
                                        item.customerName,
                                        item.product?.name,
                                        item.product?.category?.name || '-',
                                        formatQuantity(item.qty),
                                        formatCurrency(item.total),
                                        formatCurrency(item.commissionValue ?? 0)
                                    ]),
                                    ...detail.scoreEntries.map(entry => [
                                        'Pontuação',
                                        formatDateTime(entry.date),
                                        entry.customerName,
                                        entry.serviceName,
                                        '-',
                                        formatQuantity(entry.qty),
                                        '-',
                                        entry.totalScore
                                    ]),
                                    ...detail.serviceCounts.map(item => [
                                        'Serviços realizados',
                                        '-',
                                        '-',
                                        item.productName,
                                        '-',
                                        formatQuantity(item.quantity),
                                        item.total ? formatCurrency(item.total) : '-',
                                        '-'
                                    ])
                                ]}
                                disabled={loadingDetail}
                            />
                            {/* Mesma forma e mesmo tamanho dos botões de exportação ao lado:
                                os três abrem documentos do mesmo colaborador e formam um
                                grupo, então um deles contornado quebrava a leitura da barra. */}
                            <Button
                                variant="primary"
                                size="sm"
                                className="report-export-button report-export-button--summary"
                                disabled={loadingDetail}
                                onClick={() => {
                                    setDocumentItems({ services: serviceCommissionItems, products: productCommissionItems });
                                    setShowFinancialSummary(true);
                                }}
                            >
                                <span className="report-export-button__icon"><i className="fa-solid fa-file-signature"></i></span>
                                Resumo Financeiro
                            </Button>
                            {/* Só aparece quando nada do período foi pago ainda: com pagamento
                                registrado, o bloco no fim do detalhe mostra o que foi pago e
                                permite cancelar. */}
                            {canRegisterPayment && rowPayments.length === 0 && (
                                <Button
                                    variant="success"
                                    size="sm"
                                    className="report-export-button report-export-button--payment"
                                    disabled={loadingDetail}
                                    onClick={() => openPayment(row)}
                                >
                                    <span className="report-export-button__icon"><i className="fa-solid fa-money-bill-wave"></i></span>
                                    {t('cash_flow.employee_payment_register')}
                                </Button>
                            )}
                        </div>
                    )}
                    {detail && (
                        <div className="employee-statement__detail-export-title">
                            <h1>{t('employee_statement')}</h1>
                            <p>{t('employee')}: {detail.summary.employeeName}</p>
                            <p>{t('branch')}: {detail.summary.branchName || t('all_branches')}</p>
                            <p>Período: {moment(period.startDate).format('DD/MM/YYYY')} a {moment(period.endDate).format('DD/MM/YYYY')}</p>
                        </div>
                    )}
                    {loadingDetail && (
                        <div className="employee-statement__detail-loading">
                            <LoadingCenter />
                        </div>
                    )}

                    {detail && (
                        <>
                            <h5 className="employee-statement__section-title">{t('employee_statement_revenue_by_category')}</h5>
                            <div className="table-responsive">
                                <Table striped size="sm">
                                    <thead>
                                        <tr>
                                            <th>{t('category')}</th>
                                            <th className="text-end">{t('revenue_generated')}</th>
                                            <th className="text-end">{t('commission')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categoryTotals.map(([categoryName, values]) => (
                                            <tr key={categoryName}>
                                                <td>{categoryName}</td>
                                                <td className="text-end">{formatCurrency(values.revenue)}</td>
                                                <td className="text-end"><strong>{formatCurrency(values.commission)}</strong></td>
                                            </tr>
                                        ))}
                                        {categoryTotals.length === 0 && (
                                            <tr><td colSpan={3} className="text-center py-3">{t('message.no_records_found')}</td></tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <th>{t('total')}</th>
                                            <th className="text-end">
                                                {formatCurrency(serviceCommissionItems.reduce((total, item) => total + Number(item.total || 0), 0))}
                                            </th>
                                            <th className="text-end">
                                                {formatCurrency(serviceCommissionItems.reduce((total, item) => total + Number(item.commissionValue || 0), 0))}
                                            </th>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>

                            <h5 className="employee-statement__section-title">Avulsos</h5>
                            <div className="table-responsive">
                                <Table striped size="sm">
                                    <thead>
                                        <tr>
                                            <th>{t('date_time')}</th>
                                            <th>{t('customer')}</th>
                                            <th>{t('service')}</th>
                                            <th>{t('category')}</th>
                                            <th className="text-end">{t('total')}</th>
                                            <th className="text-end">Comissão</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {serviceCommissionItems.map(item => (
                                            <tr key={item.id}>
                                                <td>{formatDateTime(item.createdAt)}</td>
                                                <td>{item.customerName}</td>
                                                <td>{formatQuantity(item.qty)} x {item.product?.name}</td>
                                                <td>{item.product?.category?.name || '-'}</td>
                                                <td className="text-end">
                                                    {formatCurrency(item.total)}
                                                    {/* Com cupom no pedido o percentual incide sobre a parte que coube
                                                        a este item, então a base vai explícita para a linha fechar. */}
                                                    {item.commissionBase != null && item.commissionBase !== item.total && (
                                                        <small className="d-block text-muted">
                                                            {t('commission_base')} {formatCurrency(item.commissionBase)}
                                                        </small>
                                                    )}
                                                </td>
                                                <td className="text-end">
                                                    <strong>{formatCurrency(item.commissionValue ?? 0)}</strong>
                                                    {/* Percentual nulo é ausência de regra cadastrada, diferente de 0% deliberado. */}
                                                    <small className="d-block text-muted">
                                                        {item.commission != null
                                                            ? `Percentual aplicado: ${item.commission}%`
                                                            : `Percentual aplicado: ${t('gamification.commission_no_rule')}`}
                                                    </small>
                                                </td>
                                            </tr>
                                        ))}
                                {serviceCommissionItems.length === 0 && (
                                            <tr><td colSpan={6} className="text-center py-3">{t('message.no_records_found')}</td></tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <th colSpan={4}>{t('total')}</th>
                                            <th className="text-end">
                                {formatCurrency(serviceCommissionItems.reduce((total, item) => total + Number(item.total || 0), 0))}
                                            </th>
                                            <th className="text-end">
                                {formatCurrency(serviceCommissionItems.reduce((total, item) => total + Number(item.commissionValue || 0), 0))}
                                            </th>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>

                            <h5 className="employee-statement__section-title">Produtos</h5>
                            <div className="table-responsive">
                                <Table striped size="sm">
                                    <thead>
                                        <tr>
                                            <th>{t('date_time')}</th>
                                            <th>{t('customer')}</th>
                                            <th>{t('product')}</th>
                                            <th className="text-end">{t('quantity')}</th>
                                            <th className="text-end">{t('total')}</th>
                                            <th className="text-end">Comissão</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productCommissionItems.map(item => (
                                            <tr key={`product-${item.id}`}>
                                                <td>{formatDateTime(item.createdAt)}</td>
                                                <td>{item.customerName}</td>
                                                <td>{item.product?.name}</td>
                                                <td className="text-end">{formatQuantity(item.qty)}</td>
                                                <td className="text-end">{formatCurrency(item.total)}</td>
                                                <td className="text-end"><strong>{formatCurrency(item.commissionValue ?? 0)}</strong></td>
                                            </tr>
                                        ))}
                                        {productCommissionItems.length === 0 && (
                                            <tr><td colSpan={6} className="text-center py-3">{t('message.no_records_found')}</td></tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <th colSpan={4}>{t('total')}</th>
                                            <th className="text-end">{formatCurrency(productCommissionItems.reduce((total, item) => total + Number(item.total || 0), 0))}</th>
                                            <th className="text-end">{formatCurrency(productCommissionItems.reduce((total, item) => total + Number(item.commissionValue || 0), 0))}</th>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>

                            <h5 className="employee-statement__section-title">Assinaturas</h5>
                            <div className="table-responsive">
                                <Table striped size="sm">
                                    <thead>
                                        <tr>
                                            <th>{t('date_time')}</th>
                                            <th>{t('customer')}</th>
                                            <th>{t('service')}</th>
                                            <th className="text-end">{t('gamification.score')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detail.scoreEntries.map((entry, index) => (
                                            <tr key={index}>
                                                <td>{formatDateTime(entry.date)}</td>
                                                <td>{entry.customerName}</td>
                                                <td>{formatQuantity(entry.qty)} x {entry.serviceName}</td>
                                                <td className="text-end"><strong>{entry.totalScore}</strong></td>
                                            </tr>
                                        ))}
                                        {detail.scoreEntries.length === 0 && (
                                            <tr><td colSpan={4} className="text-center py-3">{t('message.no_records_found')}</td></tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <th colSpan={3}>{t('total')}</th>
                                            <th className="text-end">
                                                {detail.scoreEntries.reduce((total, entry) => total + Number(entry.totalScore || 0), 0)}
                                            </th>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>

                            <h5 className="employee-statement__section-title">{t('employee_statement_services')}</h5>
                            <div className="table-responsive">
                                <Table striped size="sm">
                                    <thead>
                                        <tr>
                                            <th>{t('service')}</th>
                                            <th className="text-end">{t('quantity')}</th>
                                            <th className="text-end">{t('total')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            ['Serviços avulsos', detail.serviceCounts.filter(item => item.productType === 'SERVICE' && !item.subscription)],
                                            ['Serviços de assinatura', detail.serviceCounts.filter(item => item.productType === 'SERVICE' && item.subscription)],
                                            ['Produtos', detail.serviceCounts.filter(item => item.productType === 'PRODUCT')],
                                        ].map(([groupName, groupItems]) => (
                                            <Fragment key={groupName}>
                                                <tr className="employee-statement__detail-group-row">
                                                    <th colSpan={3}>{groupName}</th>
                                                </tr>
                                                {groupItems.map(item => (
                                            <tr key={`${groupName}-${item.productId}`}>
                                                <td>{item.productName}</td>
                                                <td className="text-end">{formatQuantity(item.quantity)}</td>
                                                {/* Atendimento coberto pelo plano sai por R$ 0,00 mesmo. O zero
                                                    é o valor real da linha e soma com o total; traço pareceria
                                                    dado faltando. */}
                                                <td className="text-end">{formatCurrency(item.total)}</td>
                                            </tr>
                                                ))}
                                            </Fragment>
                                        ))}
                                        {detail.serviceCounts.length === 0 && (
                                            <tr><td colSpan={3} className="text-center py-3">{t('message.no_records_found')}</td></tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <th>{t('total')}</th>
                                            <th className="text-end">
                                                {formatQuantity(detail.serviceCounts.reduce((total, item) => total + Number(item.quantity || 0), 0))}
                                            </th>
                                            <th className="text-end">
                                                {formatCurrency(detail.serviceCounts.reduce((total, item) => total + Number(item.total || 0), 0))}
                                            </th>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>

                            {/* Acordo individual do Pote. Ganha linha própria porque é a
                                parcela que o colaborador não consegue explicar sozinho: ela
                                não vem de comanda nem de pontuação, e sem dizer de onde sai
                                (a parte da empresa) a conversa vira "então tiraram dos
                                outros". Só aparece para quem tem acordo. */}
                            {potBonusFor(detail.summary) > 0 && (
                                <>
                                    <h5 className="employee-statement__section-title">
                                        {t('pot_bonus_rules')}
                                    </h5>
                                    {/* A explicação vai como nota, no mesmo formato da dos vales,
                                        e o valor numa linha curta. Juntos na mesma linha, o texto
                                        longo empurrava o valor e alargava o painel inteiro. */}
                                    <p className="text-muted fs-12 mb-2">{t('pot_bonus_source_alert')}</p>
                                    <div className="employee-statement__advance-summary">
                                        <div className="employee-statement__advance-summary-line">
                                            <span>{t('pot_bonus_in_period')}</span>
                                            <strong>{formatCurrency(potBonusFor(detail.summary))}</strong>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Bonificações que contam no período. Mostra valor por mês e quantos
                                meses caíram no período, para o total não aparecer como um número
                                que ninguém reconhece num filtro de vários meses. Soma nos ganhos,
                                como a comissão (ver docs/cash-flow/employee-bonus). */}
                            {(detail.bonuses || []).length > 0 && (
                                <>
                                    <h5 className="employee-statement__section-title">
                                        {t('cash_flow.employee_bonuses_in_period')}
                                    </h5>
                                    <p className="text-muted fs-12 mb-2">{t('cash_flow.employee_bonuses_hint')}</p>
                                    <div className="table-responsive">
                                        <Table striped size="sm">
                                            <thead>
                                                <tr>
                                                    <th>{t('description')}</th>
                                                    <th>{t('cash_flow.employee_bonus_validity')}</th>
                                                    <th>{t('branch')}</th>
                                                    <th className="text-end">{t('cash_flow.employee_bonus_value')}</th>
                                                    <th className="text-end">{t('cash_flow.employee_bonus_months')}</th>
                                                    <th className="text-end">{t('total')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detail.bonuses.map(bonus => (
                                                    <tr key={bonus.id}>
                                                        <td>{bonus.description || '-'}</td>
                                                        <td>
                                                            {monthLabel(bonus.startMonth)}
                                                            {' a '}
                                                            {bonus.endMonth ? monthLabel(bonus.endMonth) : t('cash_flow.employee_bonus_no_end').toLowerCase()}
                                                        </td>
                                                        <td>{bonus.branchName || '-'}</td>
                                                        <td className="text-end">{formatCurrency(bonus.value)}</td>
                                                        <td className="text-end">{bonus.months}</td>
                                                        <td className="text-end">{formatCurrency(bonus.total)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <th colSpan={5}>{t('total')}</th>
                                                    <th className="text-end">{formatCurrency(detail.bonusesTotal || 0)}</th>
                                                </tr>
                                            </tfoot>
                                        </Table>
                                    </div>
                                </>
                            )}

                            {/* Vales do período. Fica depois dos ganhos e fora do total de
                                propósito: é o que o colaborador já retirou, não um desconto
                                que o sistema aplica sozinho (ver docs/cash-flow/advance). */}
                            {(detail.advances || []).length > 0 && (
                                <>
                                    <h5 className="employee-statement__section-title">
                                        {t('cash_flow.advances_in_period')}
                                    </h5>
                                    <p className="text-muted fs-12 mb-2">{t('cash_flow.advances_hint')}</p>
                                    <div className="table-responsive">
                                        <Table striped size="sm">
                                            <thead>
                                                <tr>
                                                    <th>{t('date')}</th>
                                                    <th>{t('description')}</th>
                                                    <th>{t('branch')}</th>
                                                    <th className="text-end">{t('value')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detail.advances.map(advance => (
                                                    <tr key={advance.id}>
                                                        <td>{formatDate(advance.advanceDate)}</td>
                                                        <td>{advance.description || '-'}</td>
                                                        <td>{advance.branchName || '-'}</td>
                                                        <td className="text-end">{formatCurrency(advance.value)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <th colSpan={3}>{t('total')}</th>
                                                    <th className="text-end">{formatCurrency(detail.advancesTotal || 0)}</th>
                                                </tr>
                                            </tfoot>
                                        </Table>
                                    </div>
                                </>
                            )}

                            {/* Consumos pendentes do período. Diferente do vale, mostra o
                                produto e o preço aplicado: é o que permite ao colaborador
                                reconhecer cada desconto (ver docs/cash-flow/consumption). */}
                            {(detail.consumptions || []).length > 0 && (
                                <>
                                    <h5 className="employee-statement__section-title">
                                        {t('cash_flow.consumptions_in_period')}
                                    </h5>
                                    <p className="text-muted fs-12 mb-2">{t('cash_flow.consumptions_hint')}</p>
                                    <div className="table-responsive">
                                        <Table striped size="sm">
                                            <thead>
                                                <tr>
                                                    <th>{t('date')}</th>
                                                    <th>{t('product')}</th>
                                                    <th className="text-end">{t('quantity')}</th>
                                                    <th>{t('cash_flow.consumption_price')}</th>
                                                    <th>{t('branch')}</th>
                                                    <th className="text-end">{t('value')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detail.consumptions.map(consumption => (
                                                    <tr key={consumption.id}>
                                                        <td>{formatDate(consumption.consumptionDate)}</td>
                                                        <td>{consumption.productName || '-'}</td>
                                                        <td className="text-end">{formatQuantity(consumption.quantity)}</td>
                                                        <td>
                                                            {t(`cash_flow.consumption_price_${String(consumption.priceType || 'NORMAL').toLowerCase()}`)}
                                                            {' '}({formatCurrency(consumption.unitPrice)})
                                                        </td>
                                                        <td>{consumption.branchName || '-'}</td>
                                                        <td className="text-end">{formatCurrency(consumption.total)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <th colSpan={5}>{t('total')}</th>
                                                    <th className="text-end">{formatCurrency(detail.consumptionsTotal || 0)}</th>
                                                </tr>
                                            </tfoot>
                                        </Table>
                                    </div>
                                </>
                            )}

                            {/* Fechamento do acerto: o mesmo cálculo das colunas Vales, Consumos
                                e Líquido da lista, repetido aqui porque é neste painel que o
                                gestor confere colaborador a colaborador na hora de pagar, e ele
                                não deveria ter que fechar o detalhe e voltar para a linha para
                                saber quanto sai. */}
                            {((detail.advances || []).length > 0 || (detail.consumptions || []).length > 0) && (() => {
                                const advancesTotal = Number(detail.advancesTotal || 0);
                                const consumptionsTotal = Number(detail.consumptionsTotal || 0);
                                const net = totalEarningsFor(detail.summary) - advancesTotal - consumptionsTotal;
                                return (
                                    <div className="employee-statement__advance-summary">
                                        <div className="employee-statement__advance-summary-line">
                                            <span>Ganhos no período</span>
                                            <strong>{formatCurrency(totalEarningsFor(detail.summary))}</strong>
                                        </div>
                                        {advancesTotal > 0 && (
                                            <div className="employee-statement__advance-summary-line employee-statement__advance-summary-line--advance">
                                                <span>{t('cash_flow.advances_in_period')}</span>
                                                <strong>- {formatCurrency(advancesTotal)}</strong>
                                            </div>
                                        )}
                                        {consumptionsTotal > 0 && (
                                            <div className="employee-statement__advance-summary-line employee-statement__advance-summary-line--advance">
                                                <span>{t('cash_flow.consumptions_in_period')}</span>
                                                <strong>- {formatCurrency(consumptionsTotal)}</strong>
                                            </div>
                                        )}
                                        <div className={`employee-statement__advance-summary-line employee-statement__advance-summary-line--net${net < 0 ? ' is-negative' : ''}`}>
                                            <span>Líquido a receber</span>
                                            <strong>{formatCurrency(net)}</strong>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Pagamentos registrados que cobrem o período. O valor pago é uma
                                cópia do momento do pagamento: se algo foi lançado depois (um
                                vale, uma venda), o Líquido atual muda e a diferença aparece
                                aqui, em vez de ninguém perceber. */}
                            {rowPayments.length > 0 && (
                                <div className="employee-statement__payments">
                                    <h5 className="employee-statement__section-title">
                                        {t('cash_flow.employee_payment_registered')}
                                    </h5>
                                    {rowPayments.map(payment => {
                                        const samePeriod = payment.periodStart === period.startDate
                                            && payment.periodEnd === period.endDate;
                                        const difference = netEarningsFor(row) - Number(payment.netValue || 0);
                                        return (
                                            <div key={payment.id} className="employee-statement__payment">
                                                <div className="employee-statement__payment-info">
                                                    <strong>
                                                        <i className="fa-solid fa-circle-check me-2"></i>
                                                        {formatCurrency(payment.netValue)}
                                                    </strong>
                                                    <span>
                                                        {t('cash_flow.employee_payment_paid_on', { date: formatDate(payment.paymentDate) })}
                                                        {' · '}{t(payment.paymentMethod === 'PIX' ? 'pix' : 'cash')}
                                                        {' · '}{payment.category?.name}
                                                    </span>
                                                    <span>
                                                        {t('cash_flow.employee_payment_period_range', {
                                                            start: formatDate(payment.periodStart),
                                                            end: formatDate(payment.periodEnd)
                                                        })}
                                                    </span>
                                                    {!samePeriod && (
                                                        <small className="employee-statement__payment-note">
                                                            {t('cash_flow.employee_payment_partial_period')}
                                                        </small>
                                                    )}
                                                    {samePeriod && !isSimulating && Math.abs(difference) >= 0.01 && (
                                                        <small className="employee-statement__payment-note is-warning">
                                                            {t('cash_flow.employee_payment_difference', {
                                                                current: formatCurrency(netEarningsFor(row)),
                                                                paid: formatCurrency(payment.netValue)
                                                            })}
                                                        </small>
                                                    )}
                                                </div>
                                                {canRegisterPayment && (
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        className="d-print-none"
                                                        onClick={() => openCancelPayment(payment)}
                                                    >
                                                        {t('cash_flow.employee_payment_cancel')}
                                                    </Button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
    };

    const renderPrintableReport = () => {
        const branchGroups = visibleSummaries.reduce((groups, item) => {
            const key = item.branchId ?? 'all';
            if (!groups[key]) groups[key] = { name: item.branchName || t('all_branches'), items: [] };
            groups[key].items.push(item);
            return groups;
        }, {});
        // Mesmo cabeçalho de dois blocos da tela. O PDF é o que circula por fora do
        // sistema, então não pode descrever as colunas de um jeito diferente do que o
        // gestor viu ao conferir.
        const columns = (
            <thead>
                <tr>
                    <th rowSpan={2}>{t('employee')}</th>
                    <th colSpan={revenueColumnCount} className="print-group print-group--revenue">Faturamento gerado</th>
                    <th colSpan={earningsColumnCount} className="print-group print-group--earnings">Ganhos do colaborador</th>
                </tr>
                <tr>
                    {categoryColumns.map(column => (
                        <th key={column.key} className="text-end">{column.name}</th>
                    ))}
                    <th className="text-end">Avulsos</th>
                    <th className="text-end">Produtos</th>
                    <th className="text-end">Assinaturas</th>
                    <th className="text-end print-emphasis">Total</th>
                    <th className="text-end print-earnings print-split">Avulsos</th>
                    <th className="text-end print-earnings">Produtos</th>
                    <th className="text-end print-earnings">Assinaturas</th>
                    {showBonus && <th className="text-end print-earnings">Adicional</th>}
                    {showEmployeeBonus && <th className="text-end print-earnings">Bonificação</th>}
                    <th className="text-end print-earnings print-emphasis">Total</th>
                    <th className="text-end print-earnings">Vales</th>
                    {showConsumptions && <th className="text-end print-earnings">Consumos</th>}
                    <th className="text-end print-earnings print-emphasis">Líquido</th>
                </tr>
            </thead>
        );

        /** Uma linha do PDF, na mesma ordem de colunas da tela. */
        const printRow = (values, Cell = 'td') => (
            <>
                {categoryColumns.map(column => (
                    <Cell key={column.key} className="text-end">{formatCurrency(categoryRevenueFor(values, column.key))}</Cell>
                ))}
                <Cell className="text-end">{formatCurrency(values.directServices)}</Cell>
                <Cell className="text-end">{formatCurrency(values.products)}</Cell>
                <Cell className="text-end">{formatCurrency(subscriptionProductionFor(values))}</Cell>
                <Cell className="text-end print-emphasis">{formatCurrency(totalProductionFor(values))}</Cell>
                <Cell className="text-end print-earnings print-split">{formatCurrency(ordinaryCommissionFor(values))}</Cell>
                <Cell className="text-end print-earnings">{formatCurrency(productCommissionFor(values))}</Cell>
                <Cell className="text-end print-earnings">{formatCurrency(subscriptionEarningsFor(values))}</Cell>
                {showBonus && <Cell className="text-end print-earnings">{formatCurrency(potBonusFor(values))}</Cell>}
                {showEmployeeBonus && <Cell className="text-end print-earnings">{formatCurrency(bonusesFor(values))}</Cell>}
                <Cell className="text-end print-earnings print-emphasis">{formatCurrency(totalEarningsFor(values))}</Cell>
                <Cell className="text-end print-earnings">{formatCurrency(advancesFor(values))}</Cell>
                {showConsumptions && <Cell className="text-end print-earnings">{formatCurrency(consumptionsFor(values))}</Cell>}
                <Cell className="text-end print-earnings print-emphasis">{formatCurrency(netEarningsFor(values))}</Cell>
            </>
        );

        return (
            <div className="employee-statement__print-report">
                <h1>{t('employee_statement')}</h1>
                <p>Período: {moment(period.startDate).format('DD/MM/YYYY')} a {moment(period.endDate).format('DD/MM/YYYY')}</p>
                {employeeFilter && <p>{t('employee')}: {employeeFilter.label}</p>}

                {Object.values(branchGroups).map(group => (
                    <section className="employee-statement__print-branch" key={group.name}>
                        <h2>{group.name}</h2>
                        <Table bordered striped size="sm">
                            {columns}
                            <tbody>
                                {group.items.map(item => (
                                    <tr key={`${item.employeeId}-${item.branchId}`}>
                                        <td>{item.employeeName}</td>
                                        {printRow(item)}
                                    </tr>
                                ))}
                                <tr>
                                    <th>{t('total')} {group.name}</th>
                                    {printRow(branchTotals[group.items[0].branchId ?? 'all'], 'th')}
                                </tr>
                            </tbody>
                        </Table>
                    </section>
                ))}

                <h2 className="employee-statement__print-total-title">{t('total')}</h2>
                <Table bordered size="sm">
                    {columns}
                    <tbody>
                        <tr>
                            <th>Total geral</th>
                            {printRow(totals, 'th')}
                        </tr>
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <>
            <PageTitle activeMenu={t('employee_statement')} motherMenu={t('reports')} />

            <p className="employee-statement__subtitle">{t('employee_statement_hint')}</p>

            <div className="employee-statement__filters">
                <Row className="g-3 align-items-end">
                    <Col xl={3} md={6}>
                        <Form.Group>
                            <Form.Label>{t('date_start')}</Form.Label>
                            <Form.Control
                                type="date"
                                value={period.startDate}
                                onChange={(e) => setPeriod(prev => ({ ...prev, startDate: e.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                    <Col xl={3} md={6}>
                        <Form.Group>
                            <Form.Label>{t('date_end')}</Form.Label>
                            <Form.Control
                                type="date"
                                value={period.endDate}
                                onChange={(e) => setPeriod(prev => ({ ...prev, endDate: e.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                    <Col xl={3} md={6}>
                        <Form.Group>
                            <Form.Label>{t('employee')}</Form.Label>
                            <EmployeeSelect
                                branch={selectedBranch}
                                value={employeeFilter}
                                onChange={(option) => {
                                    setEmployeeFilter(option);
                                    setSelectedEmployeeId(null);
                                    setDetail(null);
                                }}
                                filterProvidesService={false}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12}>
                        <div className="employee-statement__quick-periods">
                            {Object.keys(QUICK_PERIODS).map(key => (
                                <Button
                                    key={key}
                                    size="sm"
                                    variant={isQuickPeriodActive(key) ? 'primary' : 'outline-primary'}
                                    onClick={() => applyQuickPeriod(key)}
                                >
                                    {t(`period_shortcut.${key}`)}
                                </Button>
                            ))}
                        </div>
                    </Col>
                </Row>

                {/* Opção de exibição em linha própria: não é filtro, não muda quais
                    colaboradores aparecem, só quais colunas. Dividindo a linha com os campos,
                    a caixa ficava desalinhada dos rótulos acima deles. StatusCheckBox, e não
                    Form.Check, pelo mesmo motivo do resto do sistema: o .form-check do tema
                    alinha caixa e texto pelo topo. */}
                <div className="employee-statement__display-options">
                    <StatusCheckBox
                        id="employee-statement-show-categories"
                        label={t('employee_statement_show_categories')}
                        checked={showCategories}
                        onChange={(event) => setShowCategories(event.target.checked)}
                    />
                    {/* Sem isso, marcar a opção num período sem serviço avulso não muda nada na
                        tela, e parece que a caixa não funcionou. */}
                    {showCategories && !loadingSummaries && filteredSummaries.length > 0 && categoryColumns.length === 0 && (
                        <small className="employee-statement__display-hint">
                            {t('employee_statement_no_categories')}
                        </small>
                    )}
                </div>

                {invalidPeriod && (
                    <div className="employee-statement__warning">
                        <i className="fas fa-triangle-exclamation me-2"></i>
                        {t('validation.period_start_after_end')}
                    </div>
                )}

                <div className="employee-statement__exports">
                    <ReportExportButtons
                        contentRef={reportRef}
                        headers={exportHeaders}
                        rows={exportRows}
                        preambleRows={[
                            [t('employee_statement')],
                            ['Período', `${moment(period.startDate).format('DD/MM/YYYY')} a ${moment(period.endDate).format('DD/MM/YYYY')}`],
                            ...(employeeFilter ? [[t('employee'), employeeFilter.label]] : [])
                        ]}
                        fileName="extrato-colaboradores"
                        documentTitle="extrato-colaboradores"
                        extraStyles={PRINT_STYLES}
                        disabled={visibleSummaries.length === 0}
                    />
                    {/* Ao lado das exportações, no mesmo formato: é a ação de fechamento do
                        período inteiro, como o PDF e o Excel são o registro dele. */}
                    {canRegisterPayment && (
                        <Button
                            variant="success"
                            size="sm"
                            className="report-export-button report-export-button--payment"
                            disabled={loadingSummaries || visibleSummaries.length === 0}
                            onClick={() => setShowBatchPaymentModal(true)}
                        >
                            <span className="report-export-button__icon"><i className="fa-solid fa-money-bill-wave"></i></span>
                            {t('cash_flow.employee_payment_pay_all')}
                        </Button>
                    )}
                </div>
            </div>

            {!showPot && !loadingSummaries && visibleSummaries.length > 0 && (
                <Alert variant="info" className="employee-statement__pot-notice py-2">
                    <i className="fa-solid fa-circle-info me-2"></i>
                    {branchId
                        ? t('employee_statement_pot_notice_branch')
                        : t('employee_statement_pot_notice')}
                </Alert>
            )}

            <SubscriptionRefundDetails
                refunds={subscriptionRefunds}
                show={showRefundDetails}
                setShow={setShowRefundDetails}
                baseValue={subscriptionReceived}
                formatCurrency={formatCurrency}
            />

            <div className="employee-statement__simulation">
                <Row className="g-3 align-items-end">
                    <Col xl={3} md={6}>
                        <Form.Group>
                            <Form.Label>
                                Valor recebido de assinaturas
                                {/* Só explica o valor. A diferença com o painel, quando há mensalidade
                                    devolvida, é avisada no total de Assinaturas de cada filial. */}
                                <HintTooltip text={t('employee_statement_subscription_base_hint')} />
                            </Form.Label>
                            <InputMoney
                                currency
                                fixedDecimalScale
                                value={editableSubscriptionReceived == null ? subscriptionReceived : editableSubscriptionReceived}
                                onChange={(value, _values, sourceInfo) => {
                                    // Só a digitação é simulação. O campo também avisa quando a base chega do
                                    // servidor e a máscara arredonda os centavos; tratar isso como edição ligava
                                    // o modo simulação sozinho e escondia o alerta de estornos.
                                    if (sourceInfo?.source === 'prop') return;
                                    setEditableSubscriptionReceived(value);
                                }}
                            />
                        </Form.Group>
                    </Col>
                    <Col xl={3} md={6}>
                        <Form.Group>
                            <Form.Label>Percentual do pote</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={potPercentage}
                                onChange={(event) => setPotPercentage(event.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </div>

            {loadingSummaries ? (
                <LoadingCenter className="mt-5" />
            ) : (
                <div className={`table-responsive employee-statement__table${selectedEmployeeId ? ' has-detail' : ''}${showBonus ? ' has-bonus' : ''}${showEmployeeBonus ? ' has-employee-bonus' : ''}${showConsumptions ? ' has-consumptions' : ''}${categoryColumns.length > 0 ? ' has-categories' : ''}`}>
                    {/* Com um detalhe aberto o cabeçalho fixo passa a atrapalhar: ele descreve
                        as colunas da lista, não as do painel que a pessoa está lendo, e a altura
                        travada criaria uma rolagem dentro da outra. Enquanto há detalhe aberto a
                        tabela volta a rolar junto com a página. */}
                    {/* Com as categorias ligadas a tabela passa da largura da tela e rola na
                        horizontal, então as colunas deixam de ser porcentagem e ganham largura
                        fixa (ver .has-categories no CSS). A largura total sai do número de
                        colunas de valor, que só o componente sabe. */}
                    <Table style={categoryColumns.length > 0 ? { '--statement-numeric-columns': numericColumnCount } : undefined}>
                        {/* Larguras declaradas junto com table-layout: fixed. Sem isso, as
                            tabelas do detalhe expandido reabrem o cálculo de largura da
                            tabela externa e as colunas dançam a cada linha aberta. */}
                        {/* As larguras somam 100% de propósito. Com table-layout: fixed o
                            navegador normaliza proporcionalmente o que passa disso, então uma
                            soma maior encolhe todas as colunas — foi o que aconteceu quando a
                            coluna do adicional entrou e o nome do colaborador passou a
                            invadir a coluna vizinha. */}
                        {categoryColumns.length > 0 ? (
                            <colgroup>
                                <col className="employee-statement__col--name" />
                                {Array.from({ length: numericColumnCount }).map((_, index) => (
                                    <col key={index} className="employee-statement__col--numeric" />
                                ))}
                                <col className="employee-statement__col--chevron" />
                            </colgroup>
                        ) : (
                            <colgroup>
                                <col style={{ width: `${columnWidths.name}%` }} />
                                {Array.from({ length: numericColumnCount }).map((_, index) => (
                                    <col key={index} style={{ width: `${columnWidths.numeric}%` }} />
                                ))}
                                <col style={{ width: `${columnWidths.chevron}%` }} />
                            </colgroup>
                        )}
                        {/* Cabeçalho em dois níveis. As colunas medem coisas de donos
                            diferentes: as três primeiras são dinheiro que entrou na
                            barbearia, as quatro seguintes são o que o colaborador recebe.
                            Sem essa separação, "Total bruto" e "Total líquido" pareciam o
                            mesmo valor com e sem desconto, que não é o caso. */}
                        <thead>
                            <tr className="employee-statement__group-head">
                                <th rowSpan={2} className="employee-statement__col-name">{t('employee')}</th>
                                <th colSpan={revenueColumnCount} className="text-center employee-statement__group employee-statement__group--revenue">
                                    Faturamento gerado
                                </th>
                                <th colSpan={earningsColumnCount} className="text-center employee-statement__group employee-statement__group--earnings">
                                    Ganhos do colaborador
                                </th>
                                <th rowSpan={2} aria-label={t('details')}></th>
                            </tr>
                            <tr className="employee-statement__column-head">
                                {categoryColumns.map(column => (
                                    <th key={column.key} className="text-end employee-statement__cell--category" title={t('employee_statement_category_column_hint')}>
                                        {column.name}
                                    </th>
                                ))}
                                <th className="text-end">Avulsos</th>
                                <th className="text-end">Produtos</th>
                                <th className="text-end">Assinaturas</th>
                                <th className="text-end employee-statement__cell--emphasis employee-statement__cell--total">Total</th>
                                <th className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings employee-statement__cell--split">Avulsos</th>
                                <th className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings">Produtos</th>
                                <th className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings">Assinaturas</th>
                                {showBonus && <th className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings">Adicional</th>}
                                {showEmployeeBonus && <th className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings">Bonificação</th>}
                                <th className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings employee-statement__cell--total">Total</th>
                                <th className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings">Vales</th>
                                {showConsumptions && <th className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings">Consumos</th>}
                                <th className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings employee-statement__cell--total">Líquido</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleSummaries.map((item, index) => (
                                <Fragment key={`${item.employeeId}-${item.branchId}`}>
                                {/* Linha vazia entre os grupos. Sem esse respiro o total de uma
                                    filial encosta no cabeçalho da seguinte e as duas viram um
                                    bloco só na leitura. */}
                                {index > 0 && visibleSummaries[index - 1].branchId !== item.branchId && (
                                    <tr className="employee-statement__branch-gap" aria-hidden="true">
                                    <td colSpan={columnCount}></td>
                                    </tr>
                                )}
                                {(index === 0 || visibleSummaries[index - 1].branchId !== item.branchId) && (
                                    <tr className="employee-statement__branch-row">
                                        <th colSpan={columnCount}>
                                            <div className="employee-statement__branch-heading">
                                                <span>
                                                    <i className="fas fa-building me-2"></i>{item.branchName || t('all_branches')}
                                                </span>
                                                <small>
                                                    {branchTotals[item.branchId ?? 'all'].count} {t('employee')} · Ganhos {formatCurrency(totalEarningsFor(branchTotals[item.branchId ?? 'all']))}
                                                </small>
                                            </div>
                                        </th>
                                    </tr>
                                )}
                                <tr
                                    className={`employee-statement__row ${index % 2 === 0 ? 'is-odd' : ''} ${selectedEmployeeId === item.employeeId && selectedDetailBranchId === (item.branchId ?? null) ? 'is-open' : ''}`}
                                    onClick={() => openDetail(item)}
                                >
                                    <td className="employee-statement__col-name">{item.employeeName}</td>
                                    {renderCategoryCells(item)}
                                    <td className="text-end">{formatCurrency(item.directServices)}</td>
                                    <td className="text-end">{formatCurrency(item.products)}</td>
                                    <td className="text-end">{formatCurrency(subscriptionProductionFor(item))}</td>
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--total"><strong>{formatCurrency(totalProductionFor(item))}</strong></td>
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings employee-statement__cell--split">
                                        {formatCurrency(ordinaryCommissionFor(item))}
                                    </td>
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings">{formatCurrency(productCommissionFor(item))}</td>
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings">{formatCurrency(subscriptionEarningsFor(item))}</td>
                                    {/* O acordo individual vem do backend uma vez por pessoa, na
                                        primeira filial dela: nas outras linhas o valor é zero de
                                        propósito, porque o combinado é um só. */}
                                    {showBonus && (
                                        <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings">
                                            {potBonusFor(item) > 0 ? formatCurrency(potBonusFor(item)) : '-'}
                                        </td>
                                    )}
                                    {/* "-" para quem não tem bonificação, como no adicional: zero
                                        numa coluna de exceção parece valor esquecido. */}
                                    {showEmployeeBonus && (
                                        <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings">
                                            {bonusesFor(item) > 0 ? formatCurrency(bonusesFor(item)) : '-'}
                                        </td>
                                    )}
                                    {/* A barra compara o ganho total com o maior da filial, incluindo
                                        avulsos, produtos e assinaturas. */}
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings employee-statement__cell--total employee-statement__cell--bar">
                                        <span
                                            className="employee-statement__bar"
                                            style={{ width: `${maxEarningsByBranch[item.branchId ?? 'all'] > 0
                                                ? Math.round((totalEarningsFor(item) / maxEarningsByBranch[item.branchId ?? 'all']) * 100)
                                                : 0}%` }}
                                            aria-hidden="true"
                                        />
                                        <strong>{formatCurrency(totalEarningsFor(item))}</strong>
                                    </td>
                                    <td className="text-end employee-statement__cell--advance employee-statement__cell--earnings">
                                        {advancesFor(item) > 0 ? `- ${formatCurrency(advancesFor(item))}` : formatCurrency(0)}
                                    </td>
                                    {showConsumptions && (
                                        <td className="text-end employee-statement__cell--advance employee-statement__cell--earnings">
                                            {consumptionsFor(item) > 0 ? `- ${formatCurrency(consumptionsFor(item))}` : formatCurrency(0)}
                                        </td>
                                    )}
                                    <td className={`text-end employee-statement__cell--emphasis employee-statement__cell--earnings employee-statement__cell--total${netEarningsFor(item) < 0 ? ' employee-statement__cell--negative' : ''}`}>
                                        {/* Ícone, e não texto, porque a coluna é estreita e no
                                            celular a tabela já rola na horizontal. A data vai no
                                            title para quem passar o mouse. */}
                                        {paymentsFor(item).length > 0 && (
                                            <i
                                                className="fa-solid fa-circle-check employee-statement__paid-icon"
                                                title={paymentsFor(item).map(payment => t('cash_flow.employee_payment_paid_on', { date: formatDate(payment.paymentDate) })).join(' · ')}
                                                aria-label={t('cash_flow.employee_payment_paid')}
                                            />
                                        )}
                                        <strong>{formatCurrency(netEarningsFor(item))}</strong>
                                    </td>
                                    <td className="text-end">
                                        <i className={`fa-solid ${selectedEmployeeId === item.employeeId && selectedDetailBranchId === (item.branchId ?? null) ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                    </td>
                                </tr>
                                {selectedEmployeeId === item.employeeId && selectedDetailBranchId === (item.branchId ?? null) && renderDetailRow(columnCount, item)}
                                {(!visibleSummaries[index + 1] || visibleSummaries[index + 1].branchId !== item.branchId) && (
                                    <tr className="employee-statement__branch-total-row">
                                    <td className="employee-statement__col-name"><strong>Total {item.branchName || t('all_branches')}</strong></td>
                                    {renderCategoryCells(branchTotals[item.branchId ?? 'all'])}
                                    <td className="text-end">{formatCurrency(branchTotals[item.branchId ?? 'all'].directServices)}</td>
                                    <td className="text-end">{formatCurrency(branchTotals[item.branchId ?? 'all'].products)}</td>
                                    <td className="text-end">
                                        <SubscriptionRefundAlert
                                            refunds={subscriptionRefunds}
                                            groupTotal={subscriptionProductionFor(branchTotals[item.branchId ?? 'all'])}
                                            baseValue={subscriptionReceived}
                                            groupLabel={item.branchName}
                                            hidden={isSimulating}
                                            formatCurrency={formatCurrency}
                                            onViewDetails={() => setShowRefundDetails(true)}
                                        />
                                        <strong>{formatCurrency(subscriptionProductionFor(branchTotals[item.branchId ?? 'all']))}</strong>
                                    </td>
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--total"><strong>{formatCurrency(totalProductionFor(branchTotals[item.branchId ?? 'all']))}</strong></td>
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings employee-statement__cell--split"><strong>{formatCurrency(ordinaryCommissionFor(branchTotals[item.branchId ?? 'all']))}</strong></td>
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings"><strong>{formatCurrency(productCommissionFor(branchTotals[item.branchId ?? 'all']))}</strong></td>
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings"><strong>{formatCurrency(subscriptionEarningsFor(branchTotals[item.branchId ?? 'all']))}</strong></td>
                                    {showBonus && (
                                        <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings"><strong>{formatCurrency(potBonusFor(branchTotals[item.branchId ?? 'all']))}</strong></td>
                                    )}
                                    {showEmployeeBonus && (
                                        <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings"><strong>{formatCurrency(bonusesFor(branchTotals[item.branchId ?? 'all']))}</strong></td>
                                    )}
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings employee-statement__cell--total"><strong>{formatCurrency(totalEarningsFor(branchTotals[item.branchId ?? 'all']))}</strong></td>
                                    <td className="text-end employee-statement__cell--advance employee-statement__cell--earnings">
                                        <strong>{advancesFor(branchTotals[item.branchId ?? 'all']) > 0
                                            ? `- ${formatCurrency(advancesFor(branchTotals[item.branchId ?? 'all']))}`
                                            : formatCurrency(0)}</strong>
                                    </td>
                                    {showConsumptions && (
                                        <td className="text-end employee-statement__cell--advance employee-statement__cell--earnings">
                                            <strong>{consumptionsFor(branchTotals[item.branchId ?? 'all']) > 0
                                                ? `- ${formatCurrency(consumptionsFor(branchTotals[item.branchId ?? 'all']))}`
                                                : formatCurrency(0)}</strong>
                                        </td>
                                    )}
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings employee-statement__cell--total"><strong>{formatCurrency(netEarningsFor(branchTotals[item.branchId ?? 'all']))}</strong></td>
                                    <td></td>
                                    </tr>
                                )}
                                </Fragment>
                            ))}
                            {visibleSummaries.length === 0 && (
                                <tr>
                                    <td colSpan={columnCount} className="text-center py-4">
                                        {t('message.no_records_found')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        {visibleSummaries.length > 0 && (
                            <tfoot>
                                <tr>
                                    <td className="employee-statement__col-name"><strong>Total geral</strong></td>
                                    {renderCategoryCells(totals, true)}
                                    <td className="text-end"><strong>{formatCurrency(totals.directServices)}</strong></td>
                                    <td className="text-end"><strong>{formatCurrency(totals.products)}</strong></td>
                                    <td className="text-end">
                                        {/* Com uma filial no cabeçalho o total geral é o da filial, que já tem o alerta. */}
                                        {!branchId && (
                                            <SubscriptionRefundAlert
                                                refunds={subscriptionRefunds}
                                                groupTotal={subscriptionProductionFor(totals)}
                                                baseValue={subscriptionReceived}
                                                hidden={isSimulating}
                                                formatCurrency={formatCurrency}
                                                onViewDetails={() => setShowRefundDetails(true)}
                                            />
                                        )}
                                        <strong>{formatCurrency(subscriptionProductionFor(totals))}</strong>
                                    </td>
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--total"><strong>{formatCurrency(totalProductionFor(totals))}</strong></td>
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings employee-statement__cell--split"><strong>{formatCurrency(ordinaryCommissionFor(totals))}</strong></td>
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings"><strong>{formatCurrency(productCommissionFor(totals))}</strong></td>
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings"><strong>{formatCurrency(subscriptionEarningsFor(totals))}</strong></td>
                                    {showBonus && (
                                        <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings"><strong>{formatCurrency(potBonusFor(totals))}</strong></td>
                                    )}
                                    {showEmployeeBonus && (
                                        <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings"><strong>{formatCurrency(bonusesFor(totals))}</strong></td>
                                    )}
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings employee-statement__cell--total"><strong>{formatCurrency(totalEarningsFor(totals))}</strong></td>
                                    <td className="text-end employee-statement__cell--advance employee-statement__cell--earnings">
                                        <strong>{advancesFor(totals) > 0 ? `- ${formatCurrency(advancesFor(totals))}` : formatCurrency(0)}</strong>
                                    </td>
                                    {showConsumptions && (
                                        <td className="text-end employee-statement__cell--advance employee-statement__cell--earnings">
                                            <strong>{consumptionsFor(totals) > 0 ? `- ${formatCurrency(consumptionsFor(totals))}` : formatCurrency(0)}</strong>
                                        </td>
                                    )}
                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--earnings employee-statement__cell--total"><strong>{formatCurrency(netEarningsFor(totals))}</strong></td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        )}
                    </Table>
                </div>
            )}

            <div ref={reportRef} className="employee-statement__print-source" aria-hidden="true">
                {renderPrintableReport()}
            </div>

            <EmployeeFinancialSummary
                show={showFinancialSummary}
                setShow={setShowFinancialSummary}
                detail={detail}
                period={period}
                serviceItems={documentItems.services}
                productItems={documentItems.products}
            />

            <EmployeePaymentBatchModal
                show={showBatchPaymentModal}
                setShow={setShowBatchPaymentModal}
                period={period}
                scopeBranchId={branchId}
                isSimulating={isSimulating}
                onSaved={loadPayments}
            />

            <EmployeePaymentModal
                show={showPaymentModal}
                setShow={setShowPaymentModal}
                target={paymentTarget}
                period={period}
                scopeBranchId={branchId}
                isSimulating={isSimulating}
                onSaved={loadPayments}
            />

            <EmployeePaymentCancelModal
                show={showCancelModal}
                setShow={setShowCancelModal}
                payment={cancelTarget}
                onCanceled={loadPayments}
            />

        </>
    );
};

export default EmployeeStatementReport;
